import { api } from '../client';
import type {
  WorkflowStatusGroupDto,
  WorkflowDefinitionDto,
  WorkflowInstanceDto,
  WorkflowInstanceListResponse,
} from '../generated/schema';

export interface WorkflowInstanceParams {
  status?: string;
  /** Started-date range (the api binds fromDate / toDate; task 360.5). */
  fromDate?: string;
  toDate?: string;
  pageNumber?: number;
  pageSize?: number;
  /** Task 487.5: startedDate (default, newest first). */
  sortField?: string;
  sortOrder?: 'ascend' | 'descend';
}

export async function getWorkflows(): Promise<WorkflowDefinitionDto[]> {
  return api.get<WorkflowDefinitionDto[]>('api/console/workflows', {
    searchParams: { includeStates: 'true', includeTransitions: 'true' },
  });
}

/**
 * Lightweight fetch — code + name only, no states or transitions.
 * Used to build workflowNameMap for display (e.g. fork annotations in diagrams).
 * Avoids re-fetching the full list (states + transitions) just for name lookup.
 */
export async function getWorkflowSummaries(): Promise<WorkflowDefinitionDto[]> {
  return api.get<WorkflowDefinitionDto[]>('api/console/workflows', {
    searchParams: { includeStates: 'false', includeTransitions: 'false' },
  });
}

/** Display group of every state of the active workflow versions (task 355.10). */
export async function getWorkflowStatusGroups(): Promise<WorkflowStatusGroupDto[]> {
  return api.get<WorkflowStatusGroupDto[]>('api/console/workflows/status-groups');
}

export async function getWorkflowByCode(code: string): Promise<WorkflowDefinitionDto> {
  return api.get<WorkflowDefinitionDto>(`api/console/workflows/${code}`);
}

export async function getWorkflowInstances(
  code: string,
  params: WorkflowInstanceParams,
): Promise<WorkflowInstanceListResponse> {
  return api.get<WorkflowInstanceListResponse>(`api/console/workflows/${code}/instances`, {
    searchParams: params as Record<string, string | number>,
  });
}

export async function getWorkflowInstanceById(id: string): Promise<WorkflowInstanceDto> {
  return api.get<WorkflowInstanceDto>(`api/console/workflows/instances/${id}`);
}
